import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect } from 'react';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import { useCursorTime } from 'app/agriculture/hooks/useCursorTime/useCursorTime';
import {
    useTimelineInterval,
    useTimelineViewMode,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import { getSortedIntervalBucketTimestamps } from 'app/agriculture/queries/snapshots/utils/getSortedIntervalBucketTimestamps';
import { getSnapshotBucket } from 'app/agriculture/queries/snapshots/queryFns/getSnapshotBucket';
import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import { allBucketSizes } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import {
    prefetchedSnapshotTypes,
    snapshotConfig,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { SnapshotBucketQueryKey } from 'app/agriculture/queries/snapshots/types/SnapshotBucketQueryKey';
import pLimit from 'p-limit';
import { getSnapshotsLatestBefore } from 'app/agriculture/queries/snapshots/queryFns/getSnapshotsLatestBefore';
import { useDebounce } from 'use-debounce';
import {
    cursorTimeDebounceDelay,
    cursorTimeThrottleDelay,
} from 'app/agriculture/queries/snapshots/config/consts';

const MAX_CONCURRENT_PREFETCHES = 3;
const prefetchConcurrencyLimit = pLimit(MAX_CONCURRENT_PREFETCHES);

async function prefetchBucket<T extends SnapshotType>(
    queryClient: QueryClient,
    queryKey: SnapshotBucketQueryKey<T>,
) {
    if (queryClient.getQueryState(queryKey)) return;

    await queryClient.prefetchQuery({
        queryKey,
        queryFn: getSnapshotBucket,
    });
}

async function prefetchLatestBefore<T extends SnapshotType>(
    queryClient: QueryClient,
    queryKey: SnapshotBucketQueryKey<T>,
) {
    if (queryClient.getQueryState(queryKey)) return;

    await queryClient.prefetchQuery({
        queryKey,
        queryFn: getSnapshotsLatestBefore,
    });
}

/**
 * Prefetches all snapshots within the timeline interval
 *
 * Only prefetches `prefetchedSnapshotTypes` from snapshotConfig.ts
 * Only prefetches if bucket config allows it for the current timeline view mode
 */
export function PrefetchSnapshots({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const timelineInterval = useTimelineInterval();
    const now = useThrottledNow();
    const cursorTime = useCursorTime({ throttleDelay: cursorTimeThrottleDelay }) ?? now;
    const [debouncedCursorTime] = useDebounce(cursorTime, cursorTimeDebounceDelay);
    const { id: timelineViewMode } = useTimelineViewMode();
    const farm = useSelectedFarm();

    useEffect(() => {
        let cancelled = false;

        allBucketSizes.forEach((bucketSize) => {
            const bucketTimestamps = getSortedIntervalBucketTimestamps({
                cursorTime: debouncedCursorTime,
                bucketSize,
                timelineInterval,
                timelineViewMode,
            });

            void Promise.all(
                bucketTimestamps.flatMap((bucketTimestamp) =>
                    prefetchedSnapshotTypes.map((snapshotType) =>
                        prefetchConcurrencyLimit(async () => {
                            const { shelfLife } = snapshotConfig[snapshotType];
                            if (
                                cancelled ||
                                snapshotConfig[snapshotType].bucketSize !== bucketSize
                            ) {
                                return;
                            }

                            await prefetchBucket(queryClient, [
                                snapshotType,
                                { bucketTimestamp, farmId: farm.id },
                            ]);

                            if (shelfLife === null) {
                                await prefetchLatestBefore(queryClient, [
                                    snapshotType,
                                    {
                                        bucketTimestamp,
                                        farmId: farm.id,
                                        latestBefore: true,
                                    },
                                ]);
                            }
                        }),
                    ),
                ),
            );
        });

        return () => {
            cancelled = true;
        };
    }, [farm.id, queryClient, debouncedCursorTime, timelineInterval, timelineViewMode]);

    return children;
}
